// Lógica pura de las pantallas Product Lines / Vendors: configuración por
// tipo, validaciones, coincidencias por nombre y el runner que manda a MAS90 y
// verifica por empresa. Sin JSX, para que las pantallas solo pinten.
import { checkExists, getNextNumber, sendToMas90 } from '../../services/masterDataService';
import { LIMITS, validateVendorForm } from '../Inventory/NewItemWizard/wizardLogic';
import { phoneDigits, formatPhone } from '../Maintenance/Vendors';

export const KINDS = {
    productLine: {
        kind: 'productLine',
        title: 'Product Lines',
        noun: 'product line',
        Noun: 'Product line',
        codeLabel: 'Product Line No',
        nameLabel: 'Description',
        nameMax: LIMITS.productLineDesc,
    },
    vendor: {
        kind: 'vendor',
        title: 'Vendors',
        noun: 'vendor',
        Noun: 'Vendor',
        codeLabel: 'Vendor No',
        nameLabel: 'Vendor name',
        nameMax: LIMITS.vendorName,
    },
};

const str = (v) => String(v === undefined || v === null ? '' : v).trim();
export const sameName = (a, b) => normName(a) === normName(b);

// ─── Coincidencias por nombre ────────────────────────────────────────────────
// Se compara sin mayúsculas, signos ni espacios dobles, para que "Acme Foods,
// LLC" y "ACME FOODS LLC" cuenten como el mismo nombre.
export const normName = (v) => str(v).toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
const STOP = ['LLC', 'INC', 'CO', 'CORP', 'THE', 'AND', 'DE', 'SA', 'CV', 'LTD'];
const tokens = (v) => normName(v).split(' ').filter((t) => t.length >= 3 && STOP.indexOf(t) < 0);

// 0..100: 100 igual, 80 empieza igual, 60 contiene, y si no, palabras en común.
export function nameScore(typed, candidate) {
    const a = normName(typed);
    const b = normName(candidate);
    if (!a || !b) return 0;
    if (a === b) return 100;
    if (b.indexOf(a) === 0 || a.indexOf(b) === 0) return 80;
    if (b.indexOf(a) >= 0 || a.indexOf(b) >= 0) return 60;
    const ta = tokens(a);
    const tb = tokens(b);
    if (!ta.length || !tb.length) return 0;
    const common = ta.filter((t) => tb.some((u) => u === t || (t.length >= 4 && (u.indexOf(t) === 0 || t.indexOf(u) === 0)))).length;
    return common ? Math.round((common / Math.max(ta.length, tb.length)) * 50) : 0;
}

// Coincidencias existentes para el autocompletado del nombre.
export function nameMatches(rows, typed, limit, excludeCode) {
    if (normName(typed).length < 2) return [];
    return (rows || [])
        .filter((r) => !excludeCode || r.code.toUpperCase() !== String(excludeCode).toUpperCase())
        .map((r) => ({ row: r, score: nameScore(typed, r.name) }))
        .filter((m) => m.score > 0)
        .sort((x, y) => y.score - x.score || x.row.name.localeCompare(y.row.name))
        .slice(0, limit || 8);
}

export const exactDuplicate = (rows, name, excludeCode) => (rows || []).find((r) => (
    sameName(r.name, name) && (!excludeCode || r.code.toUpperCase() !== String(excludeCode).toUpperCase())
)) || null;

// Candidatos en la otra empresa para un renombre en ambas: primero el mismo
// número, luego los nombres más parecidos al nombre ACTUAL.
export function counterpartSuggestions(otherRows, source, limit) {
    const out = [];
    const same = (otherRows || []).find((r) => r.code.toUpperCase() === source.code.toUpperCase());
    if (same) out.push({ row: same, sameCode: true, score: nameScore(source.name, same.name) });
    nameMatches(otherRows, source.name, (limit || 5) + 1).forEach((m) => {
        if (!out.some((o) => o.row.code === m.row.code) && m.score >= 25) out.push({ row: m.row, sameCode: false, score: m.score });
    });
    return out.slice(0, limit || 5);
}

// Filtro de la lista principal: por número o por nombre.
export function filterRows(rows, query) {
    const q = str(query).toUpperCase();
    if (!q) return rows;
    const qn = normName(q);
    return rows.filter((r) => r.code.toUpperCase().indexOf(q) >= 0 || (qn && normName(r.name).indexOf(qn) >= 0));
}

// ─── Validaciones ────────────────────────────────────────────────────────────
export function validateName(cfg, name) {
    if (!str(name)) return 'Required';
    if (str(name).length > cfg.nameMax) return `Max ${cfg.nameMax} characters`;
    return '';
}

// Campos del vendor: mismas reglas que el wizard, salvo que aquí el email es
// opcional (si se escribe, debe ser válido) y el número no se valida porque lo
// asigna el sistema al enviar.
export function validateVendorFields(form) {
    const e = validateVendorForm({ ...form, code: 'x' });
    delete e.code;
    if (!str(form.email)) delete e.email;
    return e;
}

// Formulario de creación.
export function validateCreateForm(cfg, form, companies) {
    let e = {};
    if (cfg.kind === 'vendor') {
        e = validateVendorFields(form);
    } else {
        const n = validateName(cfg, form.name);
        if (n) e.name = n;
    }
    if (!companies.length) e.companies = 'Choose at least one company';
    return e;
}

export const emptyCreateForm = (kind) => (kind === 'vendor'
    ? { name: '', address: '', city: '', state: '', zip: '', phone: '', email: '' }
    : { name: '' });

export const toPayloadForm = (form, code) => ({
    ...form,
    code,
    name: str(form.name),
    address: str(form.address),
    city: str(form.city),
    state: str(form.state).toUpperCase(),
    zip: str(form.zip),
    email: str(form.email),
    phoneDigits: phoneDigits(form.phone),
});

// Formulario de edición del vendor, precargado con lo que hay en el mirror.
export const vendorFormFromRow = (row) => ({
    name: row.name || '',
    address: row.address || '',
    city: row.city || '',
    state: str(row.state).toUpperCase().slice(0, 2),
    zip: row.zip || '',
    phone: row.phone ? formatPhone(row.phone) : '',
    email: row.email || '',
});

// Campos del vendor que se comparan contra el mirror (sin mayúsculas ni signos;
// el teléfono solo por dígitos).
const VENDOR_COMPARE = [
    ['name', 'Name'], ['address', 'Address'], ['city', 'City'], ['state', 'State'], ['zip', 'Zip'], ['phone', 'Telephone'], ['email', 'Email'],
];
const sameField = (field, a, b) => (field === 'phone' ? phoneDigits(a) === phoneDigits(b) : normName(a) === normName(b));

// Lista de campos que NO coinciden entre un registro y el formulario.
export function differences(kind, record, form) {
    if (!record) return ['record'];
    if (kind !== 'vendor') return sameName(record.name, form.name) ? [] : ['Description'];
    return VENDOR_COMPARE.filter(([f]) => !sameField(f, record[f], form[f])).map(([, label]) => label);
}

// Sage llena City y State con su tabla de ZIP codes (SY_ZipCode) al importar,
// encima de lo que se mandó. Si SOLO difieren esos dos campos, se da por bueno
// y se avisa al usuario (zipNote) en lugar de marcarlo como fallo.
const ZIP_FIELDS = ['City', 'State'];
export const onlyZipChanges = (kind, diffs) => kind === 'vendor' && diffs.length > 0 && diffs.every((d) => ZIP_FIELDS.indexOf(d) >= 0);
const zipNoteOf = (record) => ({ city: record.city, state: record.state, zip: record.zip });

// ─── Tareas por empresa ──────────────────────────────────────────────────────
// status: idle (Not started) → checking / sending / verifying → done | failed.
// runs: cuántas veces se lanzó. Al segundo fallo queda `final` (sin Retry) y se
// pide reportarlo a soporte de TI.
export const MAX_RUNS = 2;
export const emptyTask = (code) => ({ status: 'idle', code: code || '', runs: 0, sent: false, final: false, message: '', alreadyInMas90: false, zipNote: null });
export const BUSY = ['checking', 'sending', 'verifying'];
export const isBusy = (t) => !!t && BUSY.indexOf(t.status) >= 0;
export const SUPPORT_MESSAGE = 'It failed again. Please send a message to IT support to report this incident, with a screenshot of this screen.';

// Un proceso completo para UNA empresa:
// 1) (crear) pide el número si aún no tiene; 2) revisa el mirror: si ya está
//    con los mismos datos se da por hecho y NO se reenvía (así un Retry nunca
//    duplica); al crear, si el número ya lo usa otro registro, pide otro;
// 3) manda a /Test/EnvioOrden (responde al terminar Sage + sync del mirror);
// 4) verifica una vez en el mirror que quedó con los datos enviados.
// form: datos ya limpios (toPayloadForm) SIN el código; el código va en task.code.
export async function runCompanyTask({ mode, kind, idCompany, form, task, onUpdate, isCancelled }) {
    let current = { ...task, runs: task.runs + 1, message: '', alreadyInMas90: false, zipNote: null };
    const update = (patch) => {
        current = { ...current, ...patch };
        onUpdate(current);
        return current;
    };
    const fail = (reason) => (current.runs >= MAX_RUNS
        ? update({ status: 'failed', final: true, message: `${reason} ${SUPPORT_MESSAGE}` })
        : update({ status: 'failed', message: `${reason} Please try again.` }));

    update({ status: 'checking' });

    // 1–2) Número + revisión previa en el mirror.
    let pre = null;
    for (let tries = 0; tries < 2; tries++) {
        if (mode === 'create' && !current.code) {
            const n = await getNextNumber(kind, idCompany);
            if (isCancelled()) return current;
            if (!n.ok) return fail(`${n.message}.`);
            update({ code: n.value });
        }
        pre = await checkExists(kind, idCompany, current.code);
        if (isCancelled()) return current;
        if (!pre.ok) return fail(`Could not check ${current.code} (${pre.message || 'no response'}). Nothing was sent.`);
        if (pre.exists) {
            const preDiffs = differences(kind, pre.record, form);
            if (!preDiffs.length) return update({ status: 'done', alreadyInMas90: true });
            // Un Retry después de que Sage ajustó City/State por el ZIP: ya quedó.
            if (current.sent && onlyZipChanges(kind, preDiffs)) return update({ status: 'done', alreadyInMas90: true, zipNote: zipNoteOf(pre.record) });
        }
        if (mode === 'edit' && !pre.exists) return fail(`${current.code} was not found in ${idCompany === 6 ? 'Altitude' : 'Mayaland'}. Nothing was sent.`);
        if (mode === 'create' && pre.exists) {
            // Ese número ya lo tiene otro registro: nunca se manda (sería un
            // upsert encima de él). Se pide otro número una vez.
            if (tries === 0) { update({ code: '' }); continue; }
            return fail(`${current.code} is already used by "${pre.record ? pre.record.name : ''}". Nothing was sent.`);
        }
        break;
    }

    // 3) Envío.
    update({ status: 'sending' });
    const sent = await sendToMas90(kind, idCompany, { ...form, code: current.code });
    if (isCancelled()) return current;
    update({ sent: true });

    // 4) Verificación en el mirror (siempre, aunque el envío haya respondido error
    //    o timeout: pudo haberse aplicado).
    update({ status: 'verifying' });
    const res = await checkExists(kind, idCompany, current.code);
    if (isCancelled()) return current;
    if (res.ok && res.exists) {
        const diffs = differences(kind, res.record, form);
        if (!diffs.length) return update({ status: 'done' });
        if (onlyZipChanges(kind, diffs)) return update({ status: 'done', zipNote: zipNoteOf(res.record) });
    }

    const sendNote = sent.ok ? '' : sent.timedOut ? ' MAS90 took too long to answer.' : ` MAS90 answered: ${sent.message || 'error'}.`;
    if (!res.ok) return fail(`Could not confirm ${current.code} (${res.message || 'no response'}).${sendNote}`);
    if (!res.exists) return fail(`${current.code} was not found after sending.${sendNote}`);
    return fail(`${current.code} was saved with different data (${differences(kind, res.record, form).join(', ')}).${sendNote}`);
}
